/**
 * Vista 11 · Entidades consultables.
 *
 * Es la vista que alimenta el modal de emisión: un canal solo se puede ofrecer
 * si aquí tiene destino configurado (`NEG-7`). Por eso los cuatro chips de
 * canal son lo primero que se ve en la tabla.
 *
 * `ARQ-6`: **baja lógica, no borrado.** Una entidad desactivada sigue visible
 * porque puede ser el destino de un oficio ya cursado, y el expediente tiene
 * que poder explicar adónde se mandó.
 */
import { useId, useState } from 'react'
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogTitle,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  StatusBadge,
  Switch,
} from '@mpfn/ui'
import {
  Callout,
  DataTable,
  EmptyState,
  FilterBar,
  FormField,
  PageHeader,
  SearchBar,
  type DataTableColumn,
} from '@mpfn/patterns'
import { Building2, Plus, Upload } from 'lucide-react'
import {
  useActualizarCanal,
  useActualizarEntidad,
  useAgregarCanal,
  useCrearEntidad,
  useCrearTipoEntidad,
  useEntidades,
  usePolitica,
} from '../api/consultas.js'
import { etiquetaDe, useCatalogos } from '../api/catalogos.js'
import type { Canal, EntidadConDisponibilidad, TipoEntidad } from '../api/tipos.js'
import { SelectorConAlta } from '../componentes/SelectorConAlta.js'
import { DialogoImportarEntidades } from './DialogoImportarEntidades.js'
import { Datos } from '../componentes/Datos.js'
import { Pagina } from '../componentes/Pagina.js'
import { Interruptor } from '../componentes/Conmutadores.js'
import { DialogoConMotivo } from '../componentes/DialogoConMotivo.js'
import { avisarError, avisarExito, plural } from '../lib/avisos.js'
import { diasHabiles } from '../lib/formato.js'

const TODOS = 'todos'

export function Entidades() {
  const catalogos = useCatalogos()
  const entidades = useEntidades(true)

  const [busqueda, setBusqueda] = useState('')
  const [tipo, setTipo] = useState<string>(TODOS)
  const [creando, setCreando] = useState(false)
  const [importando, setImportando] = useState(false)
  const [detalle, setDetalle] = useState<EntidadConDisponibilidad | undefined>(undefined)

  const columnas: ReadonlyArray<DataTableColumn<EntidadConDisponibilidad>> = [
    {
      id: 'nombre',
      header: 'Entidad',
      cell: (fila) => (
        <span className="sigfisc-celda-trabajador">
          <strong>{fila.nombre}</strong>
          {fila.siglas !== undefined && (
            <span className="sigfisc-celda-trabajador__meta">{fila.siglas}</span>
          )}
        </span>
      ),
    },
    {
      id: 'tipo',
      header: 'Tipo',
      cell: (fila) => etiquetaDe(catalogos.tiposEntidad, fila.tipo),
      hideOnMobile: true,
    },
    {
      id: 'canales',
      header: 'Canales',
      cell: (fila) => (
        <span className="sigfisc-chips">
          {fila.canalesDisponibles.map((canal) => (
            <span
              key={canal.canal}
              className="sigfisc-chip-canal"
              data-habilitado={canal.habilitado ? 'si' : 'no'}
              title={`${canal.etiqueta}: ${canal.habilitado ? 'configurado' : 'no configurado'}`}
            >
              {canal.etiqueta.charAt(0)}
            </span>
          ))}
        </span>
      ),
    },
    {
      id: 'estado',
      header: 'Estado',
      cell: (fila) =>
        fila.desactivadoEn !== undefined ? (
          <StatusBadge tone="neutral" label="Desactivada" />
        ) : (
          <StatusBadge tone="good" label="Activa" />
        ),
    },
  ]

  return (
    <Pagina>
      <PageHeader
        title="Entidades consultables"
        description="Quiénes pueden confirmar la autenticidad de un documento, y por qué canal se les pregunta."
        action={
          // Dos acciones: la masiva en secundario porque es la excepción —se
          // hace una vez, al montar el catálogo— y el alta suelta en primario,
          // que es la del día a día.
          <div className="sigfisc-acciones">
            <Button
              variant="secondary"
              onClick={() => {
                setImportando(true)
              }}
            >
              <Upload size={16} aria-hidden="true" />
              Importar listado
            </Button>
            <Button
              variant="primary"
              onClick={() => {
                setCreando(true)
              }}
            >
              <Plus size={16} aria-hidden="true" />
              Nueva entidad
            </Button>
          </div>
        }
      />

      <Datos estado={entidades}>
        {(lista) => {
          const texto = busqueda.trim().toLowerCase()
          const filtradas = lista.filter((entidad) => {
            if (tipo !== TODOS && entidad.tipo !== (tipo as TipoEntidad)) return false
            if (!texto) return true
            return `${entidad.nombre} ${entidad.siglas ?? ''} ${entidad.ruc ?? ''}`
              .toLowerCase()
              .includes(texto)
          })

          if (lista.length === 0) {
            return (
              <EmptyState
                icon={Building2}
                title="Aún no hay entidades registradas"
                description="Registre la primera para poder emitir requerimientos."
                action={
                  <Button
                    variant="primary"
                    onClick={() => {
                      setCreando(true)
                    }}
                  >
                    Nueva entidad
                  </Button>
                }
              />
            )
          }

          return (
            <DataTable
              columns={columnas}
              rows={filtradas}
              rowKey={(fila) => fila.id}
              onRowClick={setDetalle}
              rowActionLabel={(fila) => `Abrir ${fila.nombre}`}
              empty={{ title: 'Ninguna entidad coincide con el filtro' }}
              toolbar={
                <FilterBar
                  primary={
                    <SearchBar
                      value={busqueda}
                      onChange={setBusqueda}
                      label="Buscar entidad"
                      placeholder="Nombre, siglas o RUC"
                    />
                  }
                  filters={
                    <Select value={tipo} onValueChange={setTipo}>
                      <SelectTrigger aria-label="Filtrar por tipo">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value={TODOS}>Todos los tipos</SelectItem>
                        {catalogos.tiposEntidad.map((opcion) => (
                          <SelectItem key={opcion.codigo} value={opcion.codigo}>
                            {opcion.etiqueta}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  }
                />
              }
            />
          )
        }}
      </Datos>

      <DialogoEntidad abierto={creando} onCambio={setCreando} />
      <DialogoImportarEntidades abierto={importando} onCambio={setImportando} />

      {detalle && (
        <DetalleEntidad
          entidad={
            (entidades.data ?? []).find((item) => item.id === detalle.id) ?? detalle
          }
          onCerrar={() => {
            setDetalle(undefined)
          }}
        />
      )}
    </Pagina>
  )
}

function DialogoEntidad({ abierto, onCambio }: { abierto: boolean; onCambio: (v: boolean) => void }) {
  const catalogos = useCatalogos()
  const crear = useCrearEntidad()
  const crearTipo = useCrearTipoEntidad()
  const idNombre = useId()
  const idSiglas = useId()
  const idTipo = useId()
  const idRuc = useId()
  const idRepresentante = useId()
  const idCargo = useId()

  const [nombre, setNombre] = useState('')
  const [siglas, setSiglas] = useState('')
  const [tipo, setTipo] = useState('')
  const [ruc, setRuc] = useState('')
  const [representante, setRepresentante] = useState('')
  const [cargo, setCargo] = useState('')

  return (
    <Dialog
      open={abierto}
      onOpenChange={(valor) => {
        if (!valor) {
          setNombre('')
          setSiglas('')
          setTipo('')
          setRuc('')
          setRepresentante('')
          setCargo('')
        }
        onCambio(valor)
      }}
    >
      <DialogContent>
        <DialogTitle>Nueva entidad</DialogTitle>
        <DialogDescription>
          Sus canales se configuran después, desde el detalle.
        </DialogDescription>

        <form
          className="sigfisc-formulario"
          onSubmit={(evento) => {
            evento.preventDefault()
            crear.mutate(
              {
                nombre: nombre.trim(),
                tipo,
                ...(siglas.trim() ? { siglas: siglas.trim() } : {}),
                ...(ruc.trim() ? { ruc: ruc.trim() } : {}),
                ...(representante.trim() ? { representanteNombre: representante.trim() } : {}),
                ...(cargo.trim() ? { representanteCargo: cargo.trim() } : {}),
              },
              {
                onSuccess: () => {
                  avisarExito('Entidad registrada.')
                  onCambio(false)
                },
                onError: (error) => {
                  avisarError(error, 'No se pudo registrar la entidad')
                },
              },
            )
          }}
        >
          <FormField id={idNombre} label="Nombre" required>
            <Input
              value={nombre}
              autoFocus
              onChange={(evento) => {
                setNombre(evento.target.value)
              }}
            />
          </FormField>

          <div className="sigfisc-fila-campos sigfisc-fila-campos--2">
            <FormField id={idSiglas} label="Siglas">
              <Input
                value={siglas}
                onChange={(evento) => {
                  setSiglas(evento.target.value)
                }}
              />
            </FormField>

            <FormField id={idRuc} label="RUC" hint="Once dígitos.">
              <Input
                value={ruc}
                inputMode="numeric"
                onChange={(evento) => {
                  setRuc(evento.target.value)
                }}
              />
            </FormField>
          </div>

          {/* A quién se dirigen los oficios: se propone al emitir y se puede
              corregir en cada uno. */}
          <div className="sigfisc-fila-campos sigfisc-fila-campos--2">
            <FormField id={idRepresentante} label="Representante" hint="A quién se dirigen los oficios.">
              <Input
                value={representante}
                onChange={(evento) => {
                  setRepresentante(evento.target.value)
                }}
              />
            </FormField>
            <FormField id={idCargo} label="Cargo del representante" hint="Por ejemplo, Rector(a).">
              <Input
                value={cargo}
                onChange={(evento) => {
                  setCargo(evento.target.value)
                }}
              />
            </FormField>
          </div>

          {/**
           * El tipo se puede CREAR desde aquí, sin abandonar el formulario.
           *
           * Los siete tipos estaban en un `CHECK` de la tabla, así que añadir
           * «academia de idiomas» exigía migración y despliegue. Ahora viven en
           * `tipos_entidad` y el administrador los amplía desde este mismo
           * desplegable — que es donde se descubre que falta uno.
           */}
          <FormField id={idTipo} label="Tipo" required>
            <SelectorConAlta
              id={idTipo}
              opciones={catalogos.tiposEntidad}
              valor={tipo}
              onCambio={setTipo}
              marcador="Elija el tipo"
              etiquetaAlta="Crear un tipo nuevo…"
              etiquetaCampo="Nombre del tipo"
              onCrear={async (etiqueta) => {
                try {
                  const creado = await crearTipo.mutateAsync(etiqueta)
                  avisarExito(`Tipo «${creado.etiqueta}» creado.`)
                  return creado
                } catch (error) {
                  avisarError(error, 'No se pudo crear el tipo')
                  throw error
                }
              }}
            />
          </FormField>

          <div className="sigfisc-dialogo__acciones">
            <DialogClose asChild>
              <Button variant="secondary" type="button">
                Cancelar
              </Button>
            </DialogClose>
            <Button
              variant="primary"
              type="submit"
              loading={crear.isPending}
              disabled={nombre.trim() === '' || tipo === ''}
            >
              Registrar entidad
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  )
}

function DetalleEntidad({
  entidad,
  onCerrar,
}: {
  entidad: EntidadConDisponibilidad
  onCerrar: () => void
}) {
  const catalogos = useCatalogos()
  const politica = usePolitica()
  const actualizar = useActualizarEntidad()
  const agregarCanal = useAgregarCanal()
  const actualizarCanal = useActualizarCanal()

  const [canalNuevo, setCanalNuevo] = useState<Canal | ''>('')
  const [destino, setDestino] = useState('')
  const [plazo, setPlazo] = useState('')
  const [oficial, setOficial] = useState(false)
  const [desactivando, setDesactivando] = useState(false)
  const [representante, setRepresentante] = useState(entidad.representanteNombre ?? '')
  const [cargo, setCargo] = useState(entidad.representanteCargo ?? '')
  const representanteCambio =
    representante.trim() !== (entidad.representanteNombre ?? '') || cargo.trim() !== (entidad.representanteCargo ?? '')

  return (
    <Dialog
      open
      onOpenChange={(abierto) => {
        if (!abierto) onCerrar()
      }}
    >
      <DialogContent>
        <DialogTitle>{entidad.nombre}</DialogTitle>
        <DialogDescription>
          {etiquetaDe(catalogos.tiposEntidad, entidad.tipo)}
          {entidad.ruc !== undefined && ` · RUC ${entidad.ruc}`}
        </DialogDescription>

        {entidad.desactivadoEn !== undefined && (
          <Callout tone="info" title="Entidad desactivada">
            No se le pueden abrir consultas nuevas. Las ya cursadas conservan su destino.
          </Callout>
        )}

        <Card>
          <CardHeader>
            <CardTitle>Destinatario de los oficios</CardTitle>
          </CardHeader>
          <CardContent className="sigfisc-formulario">
            <div className="sigfisc-fila-campos sigfisc-fila-campos--2">
              <FormField id="entidad-representante" label="Representante">
                <Input
                  value={representante}
                  onChange={(evento) => {
                    setRepresentante(evento.target.value)
                  }}
                />
              </FormField>
              <FormField id="entidad-representante-cargo" label="Cargo">
                <Input
                  value={cargo}
                  onChange={(evento) => {
                    setCargo(evento.target.value)
                  }}
                />
              </FormField>
            </div>
            <div className="sigfisc-acciones">
              <Button
                variant="secondary"
                size="sm"
                disabled={!representanteCambio}
                loading={actualizar.isPending}
                onClick={() => {
                  actualizar.mutate(
                    {
                      id: entidad.id,
                      // Vacío lo borra: el representante cambia y a veces se desconoce.
                      cambios: { representanteNombre: representante.trim(), representanteCargo: cargo.trim() },
                    },
                    {
                      onSuccess: () => {
                        avisarExito('Destinatario actualizado.', 'Los oficios ya emitidos conservan el suyo.')
                      },
                      onError: (error) => {
                        avisarError(error, 'No se pudo actualizar el destinatario')
                      },
                    },
                  )
                }}
              >
                Guardar el destinatario
              </Button>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Canales configurados</CardTitle>
          </CardHeader>
          <CardContent>
            {entidad.canales.length === 0 ? (
              <EmptyState title="Sin canales configurados" description="Sin al menos uno, no se le puede requerir." />
            ) : (
              <ul className="sigfisc-canales-lista">
                {entidad.canales.map((canal) => {
                  const efectivo =
                    canal.plazoDias ??
                    politica.data?.plazosPorCanal[canal.canal] ??
                    catalogos.canales.find((item) => item.codigo === canal.canal)?.plazoPorDefectoDias ??
                    0
                  return (
                    <li key={canal.id} className="sigfisc-canales-lista__fila">
                      <span className="sigfisc-canales__etiqueta">
                        {etiquetaDe(
                          catalogos.canales.map((item) => ({ codigo: item.codigo, etiqueta: item.etiqueta })),
                          canal.canal,
                        )}
                        {canal.esOficial && <span className="sigfisc-items__marca">oficial</span>}
                      </span>
                      <span className="sigfisc-canales__destino">{canal.destino}</span>
                      <span className="sigfisc-canales__meta">
                        {efectivo === 0 ? 'Inmediata' : diasHabiles(efectivo)}
                        {canal.plazoDias !== undefined ? ' · plazo propio' : ' · de la política'}
                      </span>
                      <Switch
                        checked={canal.desactivadoEn === undefined}
                        aria-label={`Canal activo: ${canal.destino}`}
                        onCheckedChange={(valor) => {
                          actualizarCanal.mutate(
                            { canalId: canal.id, cambios: { desactivar: !valor } },
                            {
                              onSuccess: (datos) => {
                                avisarExito(
                                  valor ? 'Canal reactivado.' : 'Canal desactivado.',
                                  datos.consultasAbiertas > 0
                                    ? `${plural(datos.consultasAbiertas, 'consulta seguía abierta', 'consultas seguían abiertas')} por este canal y continúan su curso.`
                                    : undefined,
                                )
                              },
                              onError: (error) => {
                                avisarError(error, 'No se pudo actualizar el canal')
                              },
                            },
                          )
                        }}
                      />
                    </li>
                  )
                })}
              </ul>
            )}

            <form
              className="sigfisc-formulario"
              onSubmit={(evento) => {
                evento.preventDefault()
                if (canalNuevo === '') return
                agregarCanal.mutate(
                  {
                    entidadId: entidad.id,
                    canal: {
                      canal: canalNuevo,
                      destino: destino.trim(),
                      esOficial: oficial,
                      ...(plazo !== '' ? { plazoDias: Number(plazo) } : {}),
                    },
                  },
                  {
                    onSuccess: () => {
                      avisarExito('Canal agregado.')
                      setCanalNuevo('')
                      setDestino('')
                      setPlazo('')
                      setOficial(false)
                    },
                    onError: (error) => {
                      avisarError(error, 'No se pudo agregar el canal')
                    },
                  },
                )
              }}
            >
              <div className="sigfisc-fila-campos sigfisc-fila-campos--3">
                <FormField id="canal-nuevo" label="Canal" required>
                  <Select
                    value={canalNuevo}
                    onValueChange={(valor) => {
                      setCanalNuevo(valor as Canal)
                    }}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Elija el canal" />
                    </SelectTrigger>
                    <SelectContent>
                      {catalogos.canales.map((definicion) => (
                        <SelectItem key={definicion.codigo} value={definicion.codigo}>
                          {definicion.etiqueta}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </FormField>

                <FormField
                  id="canal-destino"
                  label="Destino"
                  required
                  hint="Domicilio, correo o URL, según el canal."
                >
                  <Input
                    value={destino}
                    onChange={(evento) => {
                      setDestino(evento.target.value)
                    }}
                  />
                </FormField>

                <FormField
                  id="canal-plazo"
                  label="Plazo propio (días hábiles)"
                  hint="Vacío = el de la política."
                >
                  <Input
                    type="number"
                    min={0}
                    max={120}
                    value={plazo}
                    onChange={(evento) => {
                      setPlazo(evento.target.value)
                    }}
                  />
                </FormField>
              </div>

              <Interruptor
                etiqueta="Es el canal oficial de este tipo"
                marcado={oficial}
                onCambio={setOficial}
                descripcion="Solo puede haber uno oficial por canal; la base lo impone."
              />

              <Button
                variant="secondary"
                type="submit"
                loading={agregarCanal.isPending}
                disabled={canalNuevo === '' || destino.trim() === ''}
              >
                Agregar canal
              </Button>
            </form>
          </CardContent>
        </Card>

        <div className="sigfisc-dialogo__acciones">
          <Button
            variant="ghost"
            onClick={() => {
              setDesactivando(true)
            }}
          >
            {entidad.desactivadoEn !== undefined ? 'Reactivar entidad' : 'Desactivar entidad'}
          </Button>
          <DialogClose asChild>
            <Button variant="secondary">Cerrar</Button>
          </DialogClose>
        </div>

        <DialogoConMotivo
          abierto={desactivando}
          onCambio={setDesactivando}
          titulo={entidad.desactivadoEn !== undefined ? 'Reactivar la entidad' : 'Desactivar la entidad'}
          descripcion={entidad.nombre}
          etiquetaConfirmar={entidad.desactivadoEn !== undefined ? 'Reactivar' : 'Desactivar'}
          cargando={actualizar.isPending}
          advertencia={
            <Callout tone="info" title="No se borra">
              Las consultas ya cursadas conservan su destino: el expediente tiene que poder explicar
              adónde se mandó cada oficio.
            </Callout>
          }
          onConfirmar={() => {
            actualizar.mutate(
              { id: entidad.id, cambios: { desactivar: entidad.desactivadoEn === undefined } },
              {
                onSuccess: () => {
                  avisarExito(
                    entidad.desactivadoEn !== undefined ? 'Entidad reactivada.' : 'Entidad desactivada.',
                  )
                  setDesactivando(false)
                },
                onError: (error) => {
                  avisarError(error, 'No se pudo actualizar la entidad')
                },
              },
            )
          }}
        />
      </DialogContent>
    </Dialog>
  )
}
